import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { Error } from '../../models/error.model';
import { Message } from '../../models/message.model';
import { Company } from '../../models/company.model';
import { Order } from '../../models/order.model';
import { CompanyOrders } from '../../models/company.orders.model';
import { authToken } from '../../scripts/authentication.script';
import { convertRequirementsNumbers } from '../../scripts/api.payload.script';

export class OrderDA {
  static apiResponse: ApiResponse

  public static async getOrders(
    id_company: string,
    interval: string,
    start_date: string,
    end_date: string,
    contact_id: string,
    status: string,
    expiration: string,
    sort_field: string,
    sort_mode: string,
    group_by: string,
    page: number = 1,
    limit: number = 20,
    search: string = '',
    all: boolean = false
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyOrderList: CompanyOrders | null = null
      let companyOrderGroup: CompanyOrders | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.orders.list.data.json').then(response => { companyOrderList = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/sale.orders.group.data.json').then(response => { companyOrderGroup = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyOrderList!.id_company === id_company || companyOrderGroup!.id_company === id_company) {
        if (group_by.length === 0) {
          companyOrderList!.filter = { interval: interval, start_date: start_date, end_date: end_date, contact: contact_id, status: status, expiration: expiration, sort_field: sort_field, sort_mode: sort_mode, group_by: group_by }
        } else {
          companyOrderGroup!.filter = { interval: interval, start_date: start_date, end_date: end_date, contact: contact_id, status: status, expiration: expiration, sort_field: sort_field, sort_mode: sort_mode, group_by: group_by }
        }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: (group_by.length === 0) ? companyOrderList : companyOrderGroup
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let startOfDay: Date = new Date(new Date().setHours(0, 0, 0, 0))
      let endOfDay: Date = new Date(new Date().setHours(23, 59, 59, 999))
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders?interval=${interval}&start_date=${start_date}&end_date=${end_date}&id_contact=${contact_id}&status=${status}&expiration=${expiration}&sort_by=${sort_field}&sort_order=${sort_mode}&group_by=${group_by}&start_day=${startOfDay}&end_day=${endOfDay}${all ? '&all=true' : `&page=${page}&limit=${limit}`}${search ? `&search=${encodeURIComponent(search)}` : ''}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getOrder(
    id_company: string,
    id_order: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let order: Order | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.order.data.json').then(response => { order = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (order!.id === id_order) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: order
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders/${id_order}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postOrder(
    id_company: string,
    customer: any,
    concerning: string,
    date: string,
    valid_until: string,
    currency: any,
    foreign_currency: any,
    treasury_account: any,
    language: string,
    street: string,
    street_number: string,
    additional: string,
    administrative_area: string,
    city: string,
    zip_code: string,
    country: string,
    requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, unit: string }[] }[],
    signatures: string[],
    company_detail: { uuid: string, name: string, logo: string, address: { street: string, number: string, additional: string, administrative_area: string, city: string, zip_code: string, country: string } },
    reference: string,
    dunning_policy_id?: string | null
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        concerning: concerning,
        signatures: signatures,
        company: company_detail,
        currency: currency,
        foreign_currency: foreign_currency,
        valid_until: valid_until,
        customer: customer,
        requirements: convertRequirementsNumbers(requirements),
        treasury_account: treasury_account,
        language: language,
        reference: reference,
        created_date: date,
        delivery_address: {
          street: street,
          number: street_number,
          additional: additional,
          administrative_area: administrative_area,
          city: city,
          zip_code: zip_code,
          country: country
        }
      }
      data.dunning_policy_id = dunning_policy_id ?? null

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postOrderReference(
    id_company: string,
    id_quote: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let order: Order | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.order.data.json').then(response => { order = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (order!.quote.id === id_quote) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: order
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders/${id_quote}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.post(url, null, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async putOrder(
    id_company: string,
    id_order: string,
    concerning: string,
    date: string,
    valid_until: string,
    currency: any,
    foreign_currency: any,
    treasury_account: any,
    language: string,
    street: string,
    street_number: string,
    additional: string,
    administrative_area: string,
    city: string,
    zip_code: string,
    country: string,
    requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, unit: string }[] }[],
    signatures: string[]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let order: Order | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.order.data.json').then(response => { order = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (order!.id === id_order) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders/${id_order}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        concerning: concerning,
        signatures: signatures,
        currency: currency,
        foreign_currency: foreign_currency,
        valid_until: valid_until,
        requirements: convertRequirementsNumbers(requirements),
        treasury_account: treasury_account,
        language: language,
        created_date: date,
        delivery_address: {
          street: street,
          number: street_number,
          additional: additional,
          administrative_area: administrative_area,
          city: city,
          zip_code: zip_code,
          country: country
        }
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async putOrderStatus(
    id_company: string,
    id_order: string,
    new_status: string,
    comment?: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let order: Order | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.order.data.json').then(response => { order = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (order!.id === id_order) {
        order!.status = new_status

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: order
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders/${id_order}/status`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        status: new_status,
        ...(comment && { comment })
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async sendOrder(
    id_company: string,
    id_order: string,
    method?: string,
    message?: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let order: Order | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.order.data.json').then(response => { order = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (order!.id === id_order) {
        order!.status = 'pending'

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: order
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders/${id_order}/send`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {}
      if (method) { data.method = method }
      if (message) { data.message = message }

      await axios.post(url, Object.keys(data).length > 0 ? data : null, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getDeliveryNotes(
    id_company: string,
    id_order: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      this.apiResponse = {
        status: 200,
        message: 'Success',
        data: []
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders/${id_order}/delivery-notes`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getDeliveryNote(
    id_company: string,
    id_order: string,
    id_delivery_note: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      this.apiResponse = {
        status: 200,
        message: 'Success',
        data: null
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders/${id_order}/delivery-notes/${id_delivery_note}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async createDeliveryNote(
    id_company: string,
    id_order: string,
    delivery_date?: string,
    delivery_address_id?: string,
    id_storage_center?: string,
    auto_stock_out?: boolean,
    contents?: Array<{ requirement_id: string, content_id: string, quantity_delivered: number, id_variant?: string }>
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      this.apiResponse = {
        status: 201,
        message: 'Success',
        data: { message: 'Delivery note created' }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders/${id_order}/delivery-notes`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {}
      if (delivery_date) { data.delivery_date = delivery_date }
      if (delivery_address_id) { data.delivery_address_id = delivery_address_id }
      if (id_storage_center) { data.id_storage_center = id_storage_center }
      if (auto_stock_out !== undefined) { data.auto_stock_out = auto_stock_out }
      if (contents && contents.length > 0) { data.contents = contents }

      await axios.post(url, Object.keys(data).length > 0 ? data : null, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async sendDeliveryNote(
    id_company: string,
    id_order: string,
    id_delivery_note: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      this.apiResponse = {
        status: 200,
        message: 'Success',
        data: { message: 'Delivery note sent' }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders/${id_order}/delivery-notes/${id_delivery_note}/send`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.post(url, null, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getDeliveryStatus(
    id_company: string,
    id_order: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      this.apiResponse = {
        status: 200,
        message: 'Success',
        data: { status: 'pending' }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders/${id_order}/delivery-status`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getStockAvailability(
    id_company: string,
    variant_ids: string[],
    id_storage_center?: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      this.apiResponse = {
        status: 200,
        message: 'Success',
        data: []
      }
    } else {
      const params = new URLSearchParams()
      for (const id of variant_ids) {
        params.append('variant_ids[]', id)
      }
      if (id_storage_center) {
        params.append('id_storage_center', id_storage_center)
      }
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/stock/availability?${params.toString()}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async attachVoucher(id_company: string, id_order: string, body: { id_document_inbox: string, role?: string }): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders/${id_order}/vouchers`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.post(url, body, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async uploadVoucher(id_company: string, id_order: string, file: File, role?: string): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders/${id_order}/vouchers/upload`
      const formData = new FormData()
      formData.append('file', file)
      formData.append('source', 'web')
      if (role) formData.append('role', role)
      let config: any = { headers: { Authorization: authToken(), 'Content-Type': 'multipart/form-data' } }
      const response = await axios.post(url, formData, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async deleteVoucher(id_company: string, id_order: string, index: number): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders/${id_order}/vouchers/${index}`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.delete(url, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }
}
